import { Loader, useGlobalModalContext } from '@creditsights/styleguide-web';
import { ReactElement, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { PermissionEnum } from '@creditsights/api-ui-web';
import { ANALYSTS_ROUTE } from '../../Constants';
import useFetchAnalyst from '../../hooks/analysts/useFetchAnalyst';
import './Analyst.scss';
import useFetchUserPermissions from '../../hooks/user/useFetchUserPermissions';
import { hasPermissionTo, hasPermissionToAskAnAnalyst } from '../../utils/permissions.utils';
import AnalystArticles from './AnalystArticles';
import AnalystCoverage from './AnalystCoverage';
import { MODAL_TYPES } from '../../components/modal/GlobalModal';
import { toasterState } from '../../atoms/toaster.atom';
import { Department } from '../../models/Analyst';

const coveragePermissionMap = {
  [Department.RESEARCH]: PermissionEnum.RESEARCH,
  [Department.NEWS]: PermissionEnum.NEWS,
  [Department.COVENANTS]: PermissionEnum.COVENANTS,
};

function Analyst(): ReactElement {
  const { analystId } = useParams();
  const { data: analyst, isLoading } = useFetchAnalyst(analystId);
  const { data: userPermissions, isLoading: isPermissionsLoading } = useFetchUserPermissions();
  const [noCoverage, setNoCoverage] = useState(false);
  const [noPublications, setNoPublications] = useState(false);
  const [expand, setExpand] = useState(false);
  const showToaster = useSetRecoilState(toasterState);
  const { showModal, setError, closeModal, store } = useGlobalModalContext();
  const handleNoPublications = () => {
    setNoPublications(true);
    setExpand(true);
  };
  const canAskAnAnalyst = !!userPermissions && hasPermissionToAskAnAnalyst(userPermissions);
  const renderDescription = () => {
    if (expand) {
      return analyst?.body;
    }
    return analyst?.body?.substring(0, 500);
  };

  const onAskAnalystModalSuccess = () => {
    return showToaster({
      text: 'Message Sent',
    });
  };

  const setAskAnalystModalError = (value: string) => {
    return setError(value);
  };

  const hasAccessToCoverage = useMemo(() => {
    for (const [department, productPermission] of Object.entries(coveragePermissionMap)) {
      const departmentCoversProduct = analyst?.positions.some((p) => p.department === department);
      const userHasAccessToProduct = hasPermissionTo(userPermissions, productPermission);

      if (departmentCoversProduct && userHasAccessToProduct) {
        return true;
      }
    }

    return false;
  }, [analyst, userPermissions]);

  const openAskAnalystModal = () => {
    showModal(MODAL_TYPES.ASK_ANALYST, {
      subject: { analystId: `${analyst?.tagId}`, subjectName: `${analyst?.displayName}` },
      onSuccess: onAskAnalystModalSuccess,
      setError: setAskAnalystModalError,
      error: store.props?.error,
      closeModal,
    });
  };

  if (isLoading || isPermissionsLoading) {
    return <Loader className="cs-loader" position="center" />;
  }
  return (
    <div className={`cs-analyst-ctx ${noCoverage || !hasAccessToCoverage ? 'no-coverage' : 'coverage'}`}>
      <NavLink to={ANALYSTS_ROUTE} className="back-link">
        <i className="angle left icon" />
        <div className="text">Back to Our Team</div>
      </NavLink>
      <div className="content-ctx">
        <div className="left-ctx">
          <div className="about-ctx">
            <div className="header-ctx">
              <div className="profile">
                {analyst?.imageUrl && <img src={analyst?.imageUrl} alt="" />}
                {!analyst?.imageUrl && <div className="no-image">{analyst?.initials}</div>}
                <div className="title-ctx">
                  <div className="title">
                    <div className="text">{analyst?.displayName}</div>
                    {analyst?.linkedInUrl && (
                      <a href={analyst?.linkedInUrl} target="_blank" rel="noreferrer">
                        <i className="linkedin icon" />
                      </a>
                    )}
                  </div>
                  <div className="role">{analyst?.role}</div>
                </div>
                <div className="actions">
                  {canAskAnAnalyst && (
                    <button onClick={openAskAnalystModal} className="ui primary big button" type="button">
                      Ask an Analyst
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="description" dangerouslySetInnerHTML={{ __html: renderDescription() || '' }} />
            {!noPublications && (
              <div className="read-more" onClick={() => setExpand((p) => !p)}>
                {expand ? 'Collapse' : 'Read More'} ...
              </div>
            )}
          </div>
          {!noPublications && <AnalystArticles analystId={analystId} noPublications={handleNoPublications} />}
        </div>
        {hasAccessToCoverage && !noCoverage && (
          <div className="right-ctx">
            <AnalystCoverage analystId={analystId} onNoCoverage={() => setNoCoverage(true)} />
          </div>
        )}
      </div>
    </div>
  );
}

export default Analyst;
